import React, { useState, useEffect, useMemo } from "react";
import {
    FiCalendar,
    FiChevronDown,
    FiChevronLeft,
    FiChevronRight,
    FiChevronUp,
    FiHome,
    FiMail,
    FiSearch,
    FiSend,
    FiUsers,
} from "react-icons/fi";
import { MdDrafts } from "react-icons/md";
import Skeleton from '../Components/SkeletonLoader'
import { getUsersSubscribers } from "../Services/userService";
import { NavLink } from "react-router-dom";
import { RoutesPaths } from "../Router/constant";

export default function Subscribermanagement() {
    const [loading, setLoading] = useState(true);
    const [userSubscriberData, setUserSubscriberData] = useState([]);
    const [offset, setOffset] = useState(1);
    const [limit] = useState(10);
    const [search, setSearch] = useState("");
    const [totalCount, setTotalCount] = useState(0);
    // Sorts the rows of the current page only; the API has no sort param.
    const [sort, setSort] = useState({ key: null, dir: "asc" });

    const fetchAllData = async () => {
        setLoading(true);
        try {
            const userSubscribersResp = await getUsersSubscribers({ offset, limit, search });
            const subscribers = userSubscribersResp.data?.data?.subscribers || [];
            setUserSubscriberData(subscribers);
            setTotalCount(userSubscribersResp?.data?.data?.count || 0);
        } catch (error) {
            console.error("Error fetching subscribers:", error);
            setUserSubscriberData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offset, search]);

    const rows = useMemo(() => {
        if (!sort.key) return userSubscriberData;
        const value = (s) =>
            sort.key === "date" ? new Date(s.createdAt).getTime() : String(s.email || "").toLowerCase();
        return [...userSubscriberData].sort((a, b) => {
            const diff = value(a) > value(b) ? 1 : value(a) < value(b) ? -1 : 0;
            return sort.dir === "asc" ? diff : -diff;
        });
    }, [userSubscriberData, sort]);

    const toggleSort = (key) =>
        setSort((prev) => ({
            key,
            dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc",
        }));

    const SortArrows = ({ column }) => (
        <span className="qb-sort-arrows">
            <FiChevronUp className={sort.key === column && sort.dir === "asc" ? "on" : ""} />
            <FiChevronDown className={sort.key === column && sort.dir === "desc" ? "on" : ""} />
        </span>
    );

    const totalPages = Math.ceil(totalCount / limit);
    const firstRow = totalCount === 0 ? 0 : (offset - 1) * limit + 1;
    const lastRow = Math.min(offset * limit, totalCount);

    return (
        <div className="container-fluid qd-page">
            {/* Breadcrumb */}
            <nav className="qd-breadcrumb">
                <NavLink to={RoutesPaths.DASHBOARD} aria-label="Dashboard">
                    <FiHome />
                </NavLink>
                <FiChevronRight />
                <span className="qd-breadcrumb-current">Newsletter management</span>
            </nav>

            {/* Banner */}
            <div className="qd-card qn-banner">
                <span className="qn-banner-icon">
                    <FiMail />
                </span>
                <div>
                    <h1 className="qn-banner-title">Newsletter details</h1>
                    <p className="qd-subtitle">
                        View and manage all users who have subscribed to your newsletter.
                    </p>
                </div>
                <div className="qn-banner-art" aria-hidden="true">
                    <span className="qn-art-blob" />
                    <MdDrafts className="qn-art-mail" />
                    <FiSend className="qn-art-plane" />
                </div>
            </div>

            {/* Subscriber list */}
            <div className="qd-card qd-panel">
                <div className="qd-panel-header">
                    <div className="qd-panel-title">
                        <span className="qd-panel-icon">
                            <FiUsers />
                        </span>
                        <div>
                            <h6>Subscriber List</h6>
                            <p className="qd-panel-sub">
                                All users who have subscribed to your newsletter are listed below.
                            </p>
                        </div>
                    </div>
                    <label className="qd-search">
                        <FiSearch />
                        <input
                            type="text"
                            placeholder="Search email..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setOffset(1);
                            }}
                        />
                    </label>
                </div>

                <div className="qd-table-wrap">
                    <table className="qd-table qn-table">
                        <thead>
                            <tr>
                                <th>
                                    <button type="button" className="qb-sort" onClick={() => toggleSort("email")}>
                                        Customer email <SortArrows column="email" />
                                    </button>
                                </th>
                                <th className="qn-date-col">
                                    <button type="button" className="qb-sort" onClick={() => toggleSort("date")}>
                                        Date <SortArrows column="date" />
                                    </button>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                [...Array(5)].map((_, i) => (
                                    <tr key={i}>
                                        <td><Skeleton width={220} /></td>
                                        <td><Skeleton width={100} /></td>
                                    </tr>
                                ))
                            ) : rows.length === 0 ? (
                                <tr>
                                    <td colSpan={2} className="qd-empty">No subscribers found</td>
                                </tr>
                            ) : (
                                rows.map((subscriber) => (
                                    <tr key={subscriber._id}>
                                        <td>
                                            <span className="qd-customer">
                                                <span className="qd-avatar">
                                                    {(subscriber.email || "?").charAt(0).toUpperCase()}
                                                </span>
                                                {subscriber.email}
                                            </span>
                                        </td>
                                        <td className="qn-date-col">
                                            <span className="qd-date">
                                                <FiCalendar />
                                                {new Date(subscriber.createdAt).toLocaleDateString('en-GB')}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {!loading && totalCount > 0 && (
                    <div className="qd-pagination">
                        <span className="qd-muted">
                            Showing {firstRow} to {lastRow} of {totalCount} subscribers
                        </span>
                        <div className="qd-pages">
                            <button disabled={offset <= 1} onClick={() => setOffset(offset - 1)} aria-label="Previous">
                                <FiChevronLeft />
                            </button>
                            {[...Array(totalPages)].map((_, index) => (
                                <button
                                    key={index}
                                    className={offset === index + 1 ? "active" : ""}
                                    onClick={() => setOffset(index + 1)}
                                >
                                    {index + 1}
                                </button>
                            ))}
                            <button disabled={offset >= totalPages} onClick={() => setOffset(offset + 1)} aria-label="Next">
                                <FiChevronRight />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
